import * as _ from "underscore";

import type UserStore from "stores/UserStore";

let stores: {
  user: UserStore;
};

export const init = (_models) => {
  stores = _models;
};

/** @returns {boolean} true if the user has notifications enabled for that language */
export const has = (name, language /* optional */) => {
  let notifications = stores.user.emailNotifications.value;
  let fullName = name;

  if (language) {
    fullName += "-" + language;
  }

  if (notifications.indexOf(fullName) > -1) {
    return true;
  }

  if (!language) {
    const languages = [];
    _.each(notifications, (reminder) => {
      const split = reminder.split("-");
      if (split[0] === name) {
        languages.push(split[1]);
      }
    });

    if (languages.length > 0) {
      return true;
    }
  }

  return false;
};

const add = (name, language) => {
  const notifications = [...stores.user.emailNotifications.value];

  let fullName = name;
  if (language) {
    fullName += "-" + language;
  }

  if (!_.contains(notifications, fullName)) {
    notifications.push(fullName);
    stores.user.save({
      emailNotifications: notifications,
    });
  }
};

const remove = (name, language) => {
  const notifications = [...stores.user.emailNotifications.value];

  let fullName = name;
  if (language) {
    fullName += "-" + language;
  }

  const index = notifications.indexOf(fullName);

  if (index !== -1) {
    notifications.splice(index, 1);
    if (notifications.length === 0) {
      // need this because pushing an empty array isn't possible via API
      notifications.push("dummy");
    }
    stores.user.save({
      emailNotifications: notifications,
    });
  }
};

export const removeAll = () => {
  stores.user.save({
    emailNotifications: ["dummy"],
  });
};

/** Returns a list of the enabled notification languages and types */
export const getEnabledNotifications = (): {
  languages: string[];
  types: string[];
} => {
  const notifications = stores.user.emailNotifications.value;

  const languages = new Set<string>();
  const types = new Set<string>();

  for (const notification of notifications) {
    const [type, language] = notification.split("-");

    if (type === "dummy") {
      continue;
    }

    if (type) {
      types.add(type);
    }
    if (language) {
      languages.add(language);
    }
  }

  return {
    languages: Array.from(languages),
    types: Array.from(types),
  };
};

/**
 * Adds a reminder for the given language for all notification types the user currently has
 * selected.
 */
export const addLanguage = (language) => {
  const { types } = getEnabledNotifications();

  // Add email reminder if no types are selected
  if (types.length === 0) {
    types.push("dailyReminder");
  }

  for (const type of types) {
    add(type, language);
  }
};

/**
 * Removes all reminders for the given language.
 */
export const removeLanguage = (language) => {
  const { types } = getEnabledNotifications();

  for (const type of types) {
    remove(type, language);
  }
};

/**
 * Adds a reminder of the given communication type for all the languages the user currently has
 * notifications enabled for.
 */
export const addType = (type) => {
  const { languages } = getEnabledNotifications();

  // Add current learning language if no languages are selected
  if (languages.length === 0) {
    languages.push(stores.user.currentLearningLanguage.value);
  }

  for (const language of languages) {
    add(type, language);
  }
};

/**
 * Removes all reminders of the given communication type for all the languages the user currently
 * has notifications enabled for.
 */
export const removeType = (type) => {
  const { languages } = getEnabledNotifications();

  for (const language of languages) {
    remove(type, language);
  }
};
